import { type OperatorNode, type AstNode } from "./types";

export const parseToAst = (expression: string): AstNode => {
  let index = 0;

  const throwError = (message: string) => {
    throw new Error(message + " at position " + (index + 1));
  };

  const advanceWhileSpace = () => {
    while (index < expression.length && /\s/.test(expression[index])) {
      index++;
    }
  };

  const getContentWithinBraces = (): string => {
    if (expression[index] !== "{") {
      throwError("Expected opening curly brace");
    }
    index++;

    let content = "";
    while (expression[index] !== "}") {
      if (index >= expression.length) {
        return throwError("Expected closing curly brace");
      }
      content += expression[index++];
    }
    index++;
    return content;
  };

  const parseNumberOrVariable = (): AstNode | never => {
    advanceWhileSpace();
    const content = getContentWithinBraces();

    if (/^\d+(\.\d+)?$/.test(content)) {
      return { type: "number", value: content };
    } else if (/^[a-z\_\d ]+$/i.test(content)) {
      return { type: "variable", value: content };
    }

    return throwError("Invalid content in curly braces: " + content);
  };

  const parseFactor = (): AstNode => {
    advanceWhileSpace();
    if (expression[index] === "(") {
      index++;
      const node = parseExpression();
      if (expression[index] !== ")") {
        throwError("Expected closing parenthesis");
      }
      index++;
      return node;
    }
    return parseNumberOrVariable();
  };

  const parseTerm = (): AstNode => {
    let node: AstNode = parseFactor();
    advanceWhileSpace();
    while (index < expression.length) {
      if (expression[index] === "*" || expression[index] === "/") {
        const op = expression[index];
        index++;
        node = {
          type: "operator",
          value: op,
          left: node,
          right: parseFactor(),
        } as OperatorNode;
      } else {
        break;
      }
    }
    advanceWhileSpace();
    return node;
  };

  const parseExpression = (): AstNode => {
    let node: AstNode = parseTerm();
    advanceWhileSpace();
    while (index < expression.length) {
      if (expression[index] === "+" || expression[index] === "-") {
        const op = expression[index];
        index++;
        node = {
          type: "operator",
          value: op,
          left: node,
          right: parseTerm(),
        } as OperatorNode;
      } else {
        break;
      }
    }
    advanceWhileSpace();
    return node;
  };

  const ast = parseExpression();
  if (index !== expression.length) {
    throwError("Unexpected characters at the end");
  }

  return ast;
};
